import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import { Button } from "src/components/ui/button";
import ContentTable, {
	type ContentTableColumn,
	type ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import UserChipItem from "src/components/UserSearchItem/UserChipItem";
import useAdminRegions from "src/features/Locations/hooks/useAdminRegions";
import useAdminAreas from "src/features/Locations/hooks/useAdminAreas";
import useAdminStores from "src/features/Locations/hooks/useAdminStores";
import usePeople from "../../hooks/usePeople";
import useArchivePerson from "../../hooks/useArchivePerson";
import { mapPeopleToContentTableItems } from "../../utils/mapPeopleToContentTableItems";

type ArchivedFilter = "all" | "active" | "archived";

const selectClassName =
	"w-full px-3 py-2.5 bg-white border border-border rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed";

const peopleColumns: ContentTableColumn[] = [
	{
		key: "email",
		label: "Email",
		render: (item) => String(item.data?.email ?? "—")
	},
	{
		key: "role",
		label: "Role",
		render: (item) => String(item.data?.role ?? "—")
	},
	{
		key: "store",
		label: "Store",
		render: (item) => String(item.data?.store ?? "—")
	},
	{
		key: "status",
		label: "Status",
		render: (item) => {
			const archived = item.data?.archived as boolean | undefined;
			return archived ? (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-500 border border-zinc-200">
					Archived
				</span>
			) : (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
					Active
				</span>
			);
		}
	}
];

const People = () => {
	const navigate = useNavigate();

	const [regionId, setRegionId] = useState<number | undefined>(undefined);
	const [areaId, setAreaId] = useState<number | undefined>(undefined);
	const [storeId, setStoreId] = useState<number | undefined>(undefined);
	const [archivedFilter, setArchivedFilter] = useState<ArchivedFilter>("all");
	const [personToArchive, setPersonToArchive] = useState<Person | null>(null);
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		debounceTimer.current = setTimeout(() => {
			setDebouncedSearch(searchInput);
		}, 300);
		return () => clearTimeout(debounceTimer.current);
	}, [searchInput]);

	const { data: regions = [] } = useAdminRegions();
	const { data: areas = [], isLoading: isLoadingAreas } =
		useAdminAreas(regionId);
	const { data: stores = [] } = useAdminStores({ regionId, areaId });

	const archivedParam =
		archivedFilter === "active"
			? false
			: archivedFilter === "archived"
				? true
				: undefined;

	const {
		data: people = [],
		isLoading,
		isFetching,
		isError
	} = usePeople({
		regionId,
		areaId,
		storeId,
		archived: archivedParam,
		search: debouncedSearch
	});

	const { mutate: archivePerson, isLoading: isArchiving } = useArchivePerson();

	const items = mapPeopleToContentTableItems(people);

	if (isError) {
		return (
			<Page>
				<p className="text-sm text-red-500">
					Something went wrong loading people.
				</p>
			</Page>
		);
	}

	return (
		<Page>
			<ContentTable
				items={items}
				isLoading={isLoading || isFetching}
				columns={peopleColumns}
				showCreatedBy={false}
				showOrder={false}
				showStatus={false}
				nameColumnLabel="Name"
				searchValue={searchInput}
				onSearchChange={setSearchInput}
				searchPlaceholder="Search people..."
				renderName={(item) => (
					<UserChipItem
						user={{
							uuid: String(item.data?.uuid ?? ""),
							id: 0,
							name: item.name,
							role: String(item.data?.role ?? ""),
							store: 0,
							employee_id: null
						}}
					/>
				)}
				onRowClick={(item) => navigate(`/users/people/${item.id}`)}
				onDelete={(item: ContentTableItem) => {
					const person = people.find((p) => p.id === item.id);
					if (person) {
						setPersonToArchive(person);
					}
				}}
				filterSlot={
					<div className="bg-white border border-border rounded-xl p-4 shadow-xs">
						<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 items-center">
							<select
								className={selectClassName}
								value={regionId ?? ""}
								onChange={(e) => {
									const val = e.target.value;
									setRegionId(val ? Number(val) : undefined);
									setAreaId(undefined);
									setStoreId(undefined);
								}}
								aria-label="Filter by region"
							>
								<option value="">All Regions</option>
								{regions.map((r) => (
									<option key={r.id} value={r.id}>
										{r.name}
									</option>
								))}
							</select>

							<select
								className={selectClassName}
								value={areaId ?? ""}
								onChange={(e) => {
									const val = e.target.value;
									setAreaId(val ? Number(val) : undefined);
									setStoreId(undefined);
								}}
								disabled={!regionId}
								aria-label="Filter by area"
							>
								<option value="">All Areas</option>
								{areas.map((a) => (
									<option key={a.id} value={a.id}>
										{a.name}
									</option>
								))}
							</select>

							<select
								className={selectClassName}
								value={storeId ?? ""}
								onChange={(e) => {
									const val = e.target.value;
									setStoreId(val ? Number(val) : undefined);
								}}
								disabled={
									!regionId || isLoadingAreas || (areas.length > 0 && !areaId)
								}
								aria-label="Filter by store"
							>
								<option value="">All Stores</option>
								{stores.map((s) => (
									<option key={s.id} value={s.id}>
										{s.name}
									</option>
								))}
							</select>

							<select
								className={selectClassName}
								value={archivedFilter}
								onChange={(e) =>
									setArchivedFilter(e.target.value as ArchivedFilter)
								}
								aria-label="Filter by status"
							>
								<option value="all">All</option>
								<option value="active">Active</option>
								<option value="archived">Archived</option>
							</select>

							<Button
								variant="outline"
								onClick={() => {
									setRegionId(undefined);
									setAreaId(undefined);
									setStoreId(undefined);
									setArchivedFilter("all");
									setSearchInput("");
									setDebouncedSearch("");
								}}
								disabled={
									!regionId &&
									!areaId &&
									!storeId &&
									archivedFilter === "all" &&
									!searchInput
								}
							>
								Reset All
							</Button>
						</div>
					</div>
				}
			/>

			<ConfirmationModal
				isOpen={personToArchive !== null}
				title={
					personToArchive?.archived
						? "Restore this person?"
						: "Archive this person?"
				}
				description="This action can be reversed at any time."
				onCancel={() => {
					if (!isArchiving) setPersonToArchive(null);
				}}
				cancelBtnProps={{ disabled: isArchiving }}
				onSubmit={() => {
					if (!personToArchive) return;
					archivePerson(
						{ id: personToArchive.id, archived: !personToArchive.archived },
						{
							onSuccess: () => setPersonToArchive(null)
						}
					);
				}}
				submitBtnProps={{ isLoading: isArchiving }}
			/>
		</Page>
	);
};

export default People;
